import React from 'react';
import classNames from 'classnames';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { IconContainer } from '../IconContainer/IconContainer';

export enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TEXT = 'text',
  SUCCESS = 'success',
  DANGER = 'danger',
  BLUE = 'blue',
  AI = 'ai',
}

const VariantToDefault: Record<ButtonVariant, string> = {
  // NOTE: primary is now identical to `blue` (blue-400). Kept as a separate variant to avoid
  // rewriting all existing PRIMARY usages for now; consolidate later.
  primary: 'text-white bg-blue-400',
  blue: 'text-white bg-blue-400',
  danger: 'text-white bg-red-500', // was 600
  secondary: 'dark:text-white light:text-black bg-surface border border-stroke',
  success: 'text-white bg-green-500', // was 600
  text: 'dark:text-white light:text-black',
  ai: 'text-white bg-ai-purple',
};

const VariantToDisabled: Record<ButtonVariant, string> = {
  blue: 'text-opacity-50! bg-opacity-50!',
  danger: 'text-opacity-50! bg-opacity-50!',
  primary: 'text-opacity-50! bg-opacity-50!',
  secondary: 'text-opacity-50! bg-opacity-50!',
  success: 'text-opacity-50! bg-opacity-50!',
  text: 'opacity-40!',
  ai: 'text-opacity-50! bg-opacity-50!',
};

const VariantToOverridenTheme: Record<ButtonVariant, Record<'light' | 'dark', string>> = {
  blue: { light: '', dark: '' },
  danger: { light: '', dark: '' },
  primary: { light: '', dark: '' },
  secondary: { light: 'text-black! bg-white! border-grey-350!', dark: 'text-white! bg-grey-900! border-grey-650!' },
  success: { light: '', dark: '' },
  text: { light: 'text-black!', dark: 'text-white!' },
  ai: { light: '', dark: '' },
};

export type ButtonProps = {
  variant: ButtonVariant;
  fullWidth?: boolean;
  icon?: IconDefinition;
  disabled?: boolean;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  overrideTheme?: 'light' | 'dark';
  textAlign?: 'left' | 'center' | 'right';
  allowTextWrap?: boolean;
  compact?: boolean;
  /** Renders the button as a pill with fully rounded ends. */
  isFullyRounded?: boolean;
};

export const Button = ({ textAlign = 'center', ...props }: ButtonProps) => {
  return (
    <div
      onClick={(event) => {
        if (!props.disabled) {
          props.onClick?.(event);
        }
      }}
      className={classNames(
        'inline-flex gap-1.5 items-center [font-weight:450] text-xs leading-4 max-w-full shrink-0',
        'select-none relative',
        props.fullWidth ? 'w-full' : 'w-fit',
        'border border-transparent',
        [VariantToDefault[props.variant]],
        {
          [VariantToDisabled[props.variant]]: props.disabled,
          'hover:cursor-pointer': !props.disabled,
          'hover:cursor-default': props.disabled,
          'pointer-events-none': props.onClick === undefined,
          'justify-start': textAlign === 'left',
          'justify-center': textAlign === 'center',
          'justify-end': textAlign === 'right',
          'h-6 px-1.5': props.compact,
          'h-8 px-3': !props.compact,
          'rounded-full': props.isFullyRounded,
          'rounded-md': !props.isFullyRounded && props.compact,
          'rounded-lg': !props.isFullyRounded && !props.compact,
        },
        props.overrideTheme ? [VariantToOverridenTheme[props.variant][props.overrideTheme]] : ''
      )}
    >
      {props.icon && <IconContainer icon={props.icon} size={12} />}
      {props.children && (
        <div className={classNames('text-overflow-ellipsis', { 'whitespace-normal': props.allowTextWrap })}>
          {props.children}
        </div>
      )}
      {/* Button background */}
      {props.variant !== ButtonVariant.TEXT && (
        <div
          className={classNames(
            'absolute bg-background w-full h-full z--1',
            props.isFullyRounded ? 'rounded-full' : props.compact ? 'rounded-md' : 'rounded-lg'
          )}
        />
      )}
    </div>
  );
};
